import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { examsApi } from '../../api/exams'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import Modal from '../../components/common/Modal'
import { FiClock, FiStar, FiHelpCircle, FiCheck } from 'react-icons/fi'
import { getStorageUrl } from '../../utils/constants'
import './ExamTaking.css'

const ExamTaking = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [textAnswers, setTextAnswers] = useState({})
  const [timeLeft, setTimeLeft] = useState(0)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const timerRef = useRef(null)

  const { data, isLoading } = useQuery({
    queryKey: ['exam-details', id],
    queryFn: () => examsApi.getExamDetails(id),
  })

  // Warn when user tries to close tab or refresh (beforeunload only works with BrowserRouter; useBlocker requires data router)
  const shouldBlockNavigation = !isSubmitted && !!data?.data
  useEffect(() => {
    if (!shouldBlockNavigation) return
    const handleBeforeUnload = (e) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [shouldBlockNavigation])

  const submitMutation = useMutation({
    // `examsApi.submitExam` expects the raw answers map: { [questionId]: answerValue }
    // It will wrap it as { answers: <map> } for the backend.
    mutationFn: (answersMap) => {
      console.log('Submitting exam with answers map:', answersMap)
      return examsApi.submitExam(id, answersMap)
    },
    onSuccess: (response) => {
      console.log('Submit response:', response)
      setShowSubmitModal(false)
      // Backend now returns {success: true, data: {...}} format
      // or {message: '...', existing_result: {...}} if already submitted
      if (response?.existing_result) {
        // Exam was already submitted, show result and redirect
        navigate(`/exams/${id}/result`)
      } else {
        // New submission successful
        navigate(`/exams/${id}/result`)
      }
    },
    onError: (error) => {
      setIsSubmitted(false) // Allow retry on error
      console.error('Exam submission error:', error)
      console.error('Error response:', error?.response?.data)
      console.error('Error status:', error?.response?.status)
      
      // Get detailed error message
      const errorData = error?.response?.data
      let errorMessage = 'حدث خطأ أثناء إرسال الإجابات'
      
      if (errorData?.message) {
        errorMessage = errorData.message
        
        // If exam already submitted, redirect to result page
        if (errorData.message === 'Exam already submitted' || errorData.message.includes('already submitted')) {
          setTimeout(() => {
            navigate(`/exams/${id}/result`)
          }, 2000)
        }
      } else if (errorData?.errors) {
        // Validation errors
        const errors = Object.values(errorData.errors).flat()
        errorMessage = errors.join(', ') || errorMessage
      } else if (error?.message) {
        errorMessage = error.message
      }
      
      alert(errorMessage)
    },
  })

  const handleSubmit = useCallback(() => {
    if (isSubmitted) return
    
    setIsSubmitted(true)
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
    
    // Format answers as {question_id: answer_value} to match backend expectations
    // Backend expects: $answers[$question->id] where question->id is an integer
    // Backend compares: $userAnswer === $question->correct_answer
    // So we need to send the answer in the same format as correct_answer
    const formattedAnswers = {}
    
    // Helper function to clean answer values (remove quotes)
    const cleanAnswerValue = (value) => {
      if (value == null || value === '') return value
      let cleaned = String(value).trim()
      // Remove surrounding quotes (both single and double)
      while ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
             (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
        cleaned = cleaned.slice(1, -1).trim()
      }
      return cleaned
    }
    
    // Add multiple choice and true/false answers
    Object.entries(answers).forEach(([questionId, answerValue]) => {
      if (answerValue != null && answerValue !== '') {
        // Clean the answer value to remove any quotes
        const cleanedValue = cleanAnswerValue(answerValue)
        
        // Use integer question ID as key to match backend expectation
        // Backend uses $question->id which is an integer
        const questionIdInt = parseInt(questionId, 10)
        if (!isNaN(questionIdInt)) {
          formattedAnswers[questionIdInt] = cleanedValue
        } else {
          // Fallback to string if conversion fails
          formattedAnswers[questionId] = cleanedValue
        }
      }
    })
    
    // Add text answers for short_answer questions
    Object.entries(textAnswers).forEach(([questionId, text]) => {
      if (text && text.trim()) {
        const questionIdInt = parseInt(questionId, 10)
        if (!isNaN(questionIdInt)) {
          formattedAnswers[questionIdInt] = text.trim()
        } else {
          formattedAnswers[questionId] = text.trim()
        }
      }
    })
    
    console.log('Formatted answers for submission:', formattedAnswers)
    console.log('Answers object:', answers)
    console.log('Text answers object:', textAnswers)
    console.log('Exam questions for comparison:', exam?.questions?.map(q => ({
      id: q.id,
      correct_answer: q.correct_answer,
      options: q.options,
      type: q.type
    })))
    
    // IMPORTANT: Pass the raw map to avoid double-wrapping { answers: { answers: ... } }
    submitMutation.mutate(formattedAnswers)
  }, [isSubmitted, answers, textAnswers, submitMutation])

  useEffect(() => {
    if (data?.data?.duration_minutes && !isSubmitted) {
      const totalSeconds = data.data.duration_minutes * 60
      setTimeLeft(totalSeconds)
      
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      
      // Start timer
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current)
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [data, isSubmitted])

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeLeft === 0 && !isSubmitted) {
      const hasAnswers = Object.keys(answers).length > 0 || 
                         Object.keys(textAnswers).filter(k => textAnswers[k]?.trim()).length > 0
      if (hasAnswers) {
        handleSubmit()
      }
    }
  }, [timeLeft, isSubmitted, answers, textAnswers, handleSubmit])

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  const exam = data?.data
  const questions = exam?.questions || []

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleAnswerChange = (questionId, answerValue) => {
    // Normalize questionId to string for consistent storage
    const normalizedQuestionId = String(questionId)
    
    // Clean the answer value - remove any surrounding quotes (both single and double)
    let cleanedValue = String(answerValue).trim()
    // Remove quotes recursively until no more quotes
    while ((cleanedValue.startsWith('"') && cleanedValue.endsWith('"')) || 
           (cleanedValue.startsWith("'") && cleanedValue.endsWith("'"))) {
      cleanedValue = cleanedValue.slice(1, -1).trim()
    }
    
    setAnswers((prev) => {
      const updated = {
        ...prev,
        [normalizedQuestionId]: cleanedValue,
      }
      // Force re-render by creating new object reference
      return { ...updated }
    })
  }

  const handleTextAnswerChange = (questionId, text) => {
    // Normalize questionId to string for consistent storage
    const normalizedQuestionId = String(questionId)
    setTextAnswers({
      ...textAnswers,
      [normalizedQuestionId]: text,
    })
  }

  const answeredCount = Object.keys(answers).filter(k => answers[k] != null && answers[k] !== '').length + 
                        Object.keys(textAnswers).filter(k => textAnswers[k]?.trim()).length
  const totalQuestions = questions.length

  return (
    <div className="exam-taking-page bg-gray-900 min-h-screen">
      <Header />
      <main className="exam-taking-main bg-gray-900">
        <div className="container mx-auto px-4">
          {/* Exam Header with Info */}
          <div className="exam-header-card">
            <div className="exam-header-content">
              <h1 className="exam-title">{exam?.title}</h1>
              {exam?.description && (
                <p className="exam-description">{exam.description}</p>
              )}
              <div className="exam-info-row">
                <div className="exam-info-item">
                  <FiClock className="exam-info-icon" />
                  <span>{exam?.duration_minutes || 0} دقيقة</span>
                </div>
                <div className="exam-info-item">
                  <FiStar className="exam-info-icon" />
                  <span>نسبة النجاح: {exam?.passing_score || 0}%</span>
                </div>
                <div className="exam-info-item">
                  <FiHelpCircle className="exam-info-icon" />
                  <span>{totalQuestions} سؤال</span>
                </div>
              </div>
            </div>
            <div className={`exam-timer-badge ${timeLeft < 60 ? 'warning' : ''}`}>
              <FiClock className="timer-icon" />
              <span className="timer-value">{formatTime(timeLeft)}</span>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="exam-progress">
            <div className="progress-info">
              <span className="progress-text">
                تم الإجابة على {answeredCount} من {totalQuestions} سؤال
              </span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div className="exam-questions">
            {questions.map((question, index) => {
              const questionType = question.type || 'multiple_choice'
              // Normalize question ID to string for consistent lookup
              const questionIdStr = String(question.id)
              // Try both string and number keys for backward compatibility
              const selectedAnswer = answers[questionIdStr] ?? answers[String(question.id)] ?? answers[question.id]
              const textAnswer = textAnswers[questionIdStr] || textAnswers[String(question.id)] || textAnswers[question.id] || ''
              
              return (
                <Card key={question.id} className="question-card">
                  <div className="question-header">
                    <div className="question-number-badge">
                      السؤال {index + 1}
                    </div>
                    <div className="question-points-badge">
                      {question.points} نقطة
                    </div>
                  </div>
                  
                  <h3 className="question-text">{question.question}</h3>
                  {question.question_image && (
                    <div className="question-image-wrap">
                      <img
                        src={getStorageUrl(question.question_image)}
                        alt=""
                        className="question-image"
                      />
                    </div>
                  )}
                  
                  {/* Multiple Choice Questions */}
                  {questionType === 'multiple_choice' && question.options && (
                    <div className="question-options">
                      {question.options.map((option, optionIndex) => {
                        const optionLabel = String.fromCharCode(65 + optionIndex) // A, B, C, D...
                        
                        // Options can be strings or objects with {id, text, value, etc.}
                        // Properly extract the text value
                        let optionText = ''
                        let optionId = null
                        
                        let optionImage = null
                        if (typeof option === 'string') {
                          optionText = option
                        } else if (option && typeof option === 'object') {
                          // Try common property names for the text
                          optionText = option.text || option.label || option.value || option.option || ''
                          optionImage = option.image || null
                          optionId = option.id || option.value || null
                          
                          // If still no text, try to get first string value from object
                          if (!optionText && option) {
                            const values = Object.values(option).filter(v => typeof v === 'string' && v)
                            optionText = values[0] || ''
                          }
                        }
                        
                        // Fallback: use option index if text is still empty
                        if (!optionText) {
                          optionText = `Option ${optionLabel}`
                        }
                        
                        // Clean the option text - remove any surrounding quotes
                        // Sometimes the text might come with quotes from the database
                        optionText = optionText.trim()
                        if ((optionText.startsWith('"') && optionText.endsWith('"')) || 
                            (optionText.startsWith("'") && optionText.endsWith("'"))) {
                          optionText = optionText.slice(1, -1)
                        }
                        
                        // The answer value we'll store - use the cleaned text content
                        // This should match what the backend expects in correct_answer
                        const answerValue = optionText
                        
                        // Check if this specific option is selected
                        // Only match if the stored answer exactly matches this option's text
                        const normalize = (val) => {
                          if (val == null) return ''
                          return String(val).trim()
                        }
                        
                        const selectedNormalized = normalize(selectedAnswer)
                        const answerNormalized = normalize(answerValue)
                        
                        // Only this specific option should be selected
                        const isSelected = selectedNormalized !== '' && selectedNormalized === answerNormalized
                        
                        return (
                          <div
                            key={`${question.id}-${optionIndex}`}
                            className={`option-item ${isSelected ? 'selected' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation()
                              // Store the option text as the answer
                              handleAnswerChange(question.id, answerValue)
                            }}
                          >
                            <div className="option-radio">
                              {isSelected && <FiCheck className="option-check" />}
                            </div>
                            <span className="option-label-char">{optionLabel}.</span>
                            <div className="option-content">
                              {optionImage && (
                                <div className="option-image-wrap">
                                  <img src={getStorageUrl(optionImage)} alt="" className="option-image" />
                                </div>
                              )}
                              <span className="option-text">{optionText}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  
                  {/* True/False Questions */}
                  {questionType === 'true_false' && (
                    <div className="question-options true-false-options">
                      <div
                        className={`option-item ${selectedAnswer?.toLowerCase() === 'true' ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleAnswerChange(question.id, 'true')
                        }}
                      >
                        <div className="option-radio">
                          {selectedAnswer?.toLowerCase() === 'true' && <FiCheck className="option-check" />}
                        </div>
                        <span className="option-text">صحيح</span>
                      </div>
                      <div
                        className={`option-item ${selectedAnswer?.toLowerCase() === 'false' ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleAnswerChange(question.id, 'false')
                        }}
                      >
                        <div className="option-radio">
                          {selectedAnswer?.toLowerCase() === 'false' && <FiCheck className="option-check" />}
                        </div>
                        <span className="option-text">خطأ</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Short Answer Questions */}
                  {questionType === 'short_answer' && (
                    <div className="question-text-input">
                      <textarea
                        className="answer-textarea"
                        placeholder="أدخل إجابتك هنا..."
                        value={textAnswer}
                        onChange={(e) => handleTextAnswerChange(question.id, e.target.value)}
                        rows={4}
                      />
                    </div>
                  )}
                </Card>
              )
            })}
          </div>

          <div className="exam-footer">
            <div className="exam-footer-content">
              <div className="exam-footer-info">
                <span className="answered-count">
                  {answeredCount} / {totalQuestions} إجابة
                </span>
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={() => setShowSubmitModal(true)}
                disabled={isSubmitted || (answeredCount === 0 && Object.keys(textAnswers).filter(k => textAnswers[k]?.trim()).length === 0)}
                loading={isSubmitted}
              >
                {isSubmitted ? 'جاري الإرسال...' : 'إرسال الإجابات'}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={showSubmitModal}
        onClose={() => !isSubmitted && setShowSubmitModal(false)}
        title=""
        size="sm"
      >
        <div className="exam-submit-modal-content">
          <h2 className="exam-submit-modal-title">تأكيد الإرسال</h2>
          <p className="exam-submit-modal-message">
            هل أنت متأكد من إرسال إجاباتك؟ لن يمكنك تعديل الإجابات بعد الإرسال.
          </p>
          <div className="exam-submit-modal-actions">
            <Button
              variant="primary"
              onClick={() => {
                setShowSubmitModal(false)
                handleSubmit()
              }}
              fullWidth
              disabled={isSubmitted}
              loading={isSubmitted}
            >
              نعم، إرسال
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSubmitModal(false)}
              fullWidth
              disabled={isSubmitted}
            >
              إلغاء
            </Button>
          </div>
        </div>
      </Modal>

      <Footer />
    </div>
  )
}

export default ExamTaking

